import { Localized, useLocalization } from "@fluent/react";
import ClearIcon from "@mui/icons-material/Clear";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
    Alert,
    Box,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector, useDataGridCustomLocaleText } from "../../../app/hooks";
import {
    clearCompanyBugDetails,
    clearMyBugDetails,
    getCompanyBugDetails,
    getCompanyBugs,
    getMyBugs,
    getMyBugDetails,
    selectSupportIntegration,
} from "../supportIntegrationSlice";
import {
    fixedStatuses,
    newStatuses,
    normalizeStatus,
    rejectedStatuses,
    reportedStatuses,
    underRepairStatuses,
} from "../helpers/companyBugHelpers";
import { BugDetailsData, BugDetailsDialog } from "./BugDetailsDialog";
import { BugBusinessCriticalChip } from "./BugBusinessCriticalChip";
import { CompanyBugPriorityChip } from "./CompanyBugPriorityChip";
import { CompanyBugStatusChip } from "./CompanyBugStatusChip";

type StatusFilter = "reported" | "new" | "underRepair" | "fixed" | "rejected" | "all";

type BugListRow = {
    id: number;
    title: string;
    priority: string;
    status: string;
    createDate: string;
    isBusinessCritical: boolean;
    reportedBy?: string | null;
};

type Props = {
    mode: "user" | "company";
    height?: string | number;
};

export const BugsList = ({ mode, height }: Props) => {
    const { l10n } = useLocalization();
    const dispatch = useAppDispatch();
    const localeText = useDataGridCustomLocaleText();
    const {
        bugs,
        loading,
        error,
        companyBugs,
        companyBugsError,
        companyBugsLoading,
        companyBugDetails,
        companyBugDetailsError,
        companyBugDetailsLoading,
        myBugDetails,
        myBugDetailsError,
        myBugDetailsLoading,
    } = useAppSelector(selectSupportIntegration);
    const isCompany = mode === "company";
    const [statusFilter, setStatusFilter] = useState<StatusFilter>("reported");
    const [dateFrom, setDateFrom] = useState<Date | null>(null);
    const [dateTo, setDateTo] = useState<Date | null>(null);
    const [detailsOpen, setDetailsOpen] = useState(false);

    useEffect(() => {
        if (isCompany) {
            dispatch(getCompanyBugs());
        } else {
            dispatch(getMyBugs());
        }
    }, [dispatch, isCompany]);

    const rows = useMemo<BugListRow[]>(() => isCompany
        ? companyBugs.map(bug => ({
            id: bug.id,
            title: bug.title,
            priority: bug.priority,
            status: bug.status ?? "",
            createDate: bug.createDate,
            isBusinessCritical: bug.isBusinessCritical,
            reportedBy: bug.reportedBy,
        }))
        : bugs.map(bug => ({
            id: bug.id,
            title: bug.title,
            priority: bug.priority,
            status: bug.status ?? "",
            createDate: bug.createDate,
            isBusinessCritical: bug.isBusinessCritical,
            reportedBy: null,
        })), [bugs, companyBugs, isCompany]);

    const filteredBugs = useMemo(() => rows.filter(bug => {
        const status = normalizeStatus(bug.status);
        const matchesStatus = statusFilter === "all"
            || (statusFilter === "reported" && reportedStatuses.has(status))
            || (statusFilter === "new" && newStatuses.has(status))
            || (statusFilter === "underRepair" && underRepairStatuses.has(status))
            || (statusFilter === "rejected" && rejectedStatuses.has(status))
            || (statusFilter === "fixed" && fixedStatuses.has(status));

        const createDate = new Date(bug.createDate);
        const startOfDate = dateFrom && new Date(dateFrom);
        const endOfDate = dateTo && new Date(dateTo);
        startOfDate?.setHours(0, 0, 0, 0);
        endOfDate?.setHours(23, 59, 59, 999);
        const matchesDateFrom = !startOfDate || createDate >= startOfDate;
        const matchesDateTo = !endOfDate || createDate <= endOfDate;

        return matchesStatus && matchesDateFrom && matchesDateTo;
    }), [rows, dateFrom, dateTo, statusFilter]);

    const openDetails = useCallback((row: BugListRow) => {
        setDetailsOpen(true);
        if (isCompany) {
            dispatch(getCompanyBugDetails(row.id));
        } else {
            dispatch(getMyBugDetails(row.id));
        }
    }, [dispatch, isCompany]);

    const handleDetailsExited = () => {
        if (isCompany) {
            dispatch(clearCompanyBugDetails());
        } else {
            dispatch(clearMyBugDetails());
        }
    };

    const columns = useMemo<GridColDef<BugListRow>[]>(() => [
        { field: "id", headerName: "ID", type: "number", flex: 0.5, minWidth: 60 },
        {
            field: "title",
            headerName: l10n.getString("bug-report-subject", null, "Subject"),
            flex: 2.5,
            minWidth: 120,
        },
        ...(isCompany ? [{
            field: "reportedBy",
            headerName: l10n.getString("company-bugs-reported-by", null, "Reported by"),
            flex: 1.5,
            minWidth: 90,
            valueGetter: (_: unknown, row: BugListRow) => row.reportedBy ?? "-",
        } as GridColDef<BugListRow>] : []),
        {
            field: "createDate",
            headerName: l10n.getString("bug-report-list-date", null, "Reported at"),
            type: "dateTime",
            flex: 1.5,
            minWidth: 110,
            valueGetter: (_, row) => new Date(row.createDate),
            renderCell: ({ row }) => new Date(row.createDate).toLocaleString(),
        },
        {
            field: "isBusinessCritical",
            headerName: l10n.getString("bug-report-business-critical", null, "Critical"),
            flex: 1,
            minWidth: 80,
            renderCell: ({ row }) => <BugBusinessCriticalChip isBusinessCritical={row.isBusinessCritical} />,
        },
        {
            field: "priority",
            headerName: l10n.getString("bug-report-list-priority", null, "Priority"),
            flex: 1,
            minWidth: 80,
            renderCell: ({ value }) => <CompanyBugPriorityChip priority={value} />,
        },
        {
            field: "status",
            headerName: l10n.getString("bug-report-list-status", null, "Status"),
            flex: 1.5,
            minWidth: 100,
            renderCell: ({ row }) => <CompanyBugStatusChip status={row.status} />,
        },
        {
            field: "actions",
            headerName: "",
            width: 56,
            sortable: false,
            filterable: false,
            renderCell: ({ row }) => (
                <IconButton
                    size="small"
                    onClick={() => openDetails(row)}
                    aria-label={l10n.getString("bug-report-list-preview", null, "Preview")}
                >
                    <VisibilityOutlinedIcon fontSize="small" />
                </IconButton>
            ),
        },
    ], [l10n, isCompany, openDetails]);

    const loadError = isCompany ? companyBugsError : error;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", height: height ?? "100%", minHeight: 0 }}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mb: 1.5 }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel id="bug-status-filter-label">
                        <Localized id="company-bugs-status-filter">Status</Localized>
                    </InputLabel>
                    <Select
                        labelId="bug-status-filter-label"
                        value={statusFilter}
                        label={l10n.getString("company-bugs-status-filter", null, "Status")}
                        onChange={event => setStatusFilter(event.target.value as StatusFilter)}
                        endAdornment={statusFilter !== "reported" && (
                            <IconButton
                                size="small"
                                sx={{ mr: 2 }}
                                onMouseDown={event => event.stopPropagation()}
                                onClick={() => setStatusFilter("reported")}
                            >
                                <ClearIcon fontSize="small" />
                            </IconButton>
                        )}
                    >
                        <MenuItem value="reported"><Localized id="company-bugs-reported">Reported</Localized></MenuItem>
                        <MenuItem value="new"><Localized id="company-bugs-new">New</Localized></MenuItem>
                        <MenuItem value="underRepair"><Localized id="company-bugs-under-repair">Under Repair</Localized></MenuItem>
                        <MenuItem value="fixed"><Localized id="company-bugs-fixed">Fixed</Localized></MenuItem>
                        <MenuItem value="rejected"><Localized id="company-bugs-rejected">Rejected</Localized></MenuItem>
                        <MenuItem value="all"><Localized id="company-bugs-all">All</Localized></MenuItem>
                    </Select>
                </FormControl>
                <DatePicker
                    label={l10n.getString("company-bugs-date-from", null, "Reported from")}
                    value={dateFrom}
                    onChange={setDateFrom}
                    slotProps={{ textField: { size: "small", sx: { minWidth: 180 } }, field: { clearable: true } }}
                />
                <DatePicker
                    label={l10n.getString("company-bugs-date-to", null, "Reported to")}
                    value={dateTo}
                    onChange={setDateTo}
                    slotProps={{ textField: { size: "small", sx: { minWidth: 180 } }, field: { clearable: true } }}
                />
            </Stack>

            {loadError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {isCompany
                        ? <Localized id="company-bugs-load-error">Company bug reports could not be loaded.</Localized>
                        : <Localized id="bug-report-list-load-error">The reported bugs could not be loaded.</Localized>}
                </Alert>
            )}

            {/* width: 0 + minWidth: 100% keeps the grid's content from stretching the flex parent, so it can shrink */}
            <Box sx={{ flex: 1, minHeight: 300, width: 0, minWidth: "100%" }}>
                <DataGrid
                    rows={filteredBugs}
                    columns={columns}
                    loading={isCompany ? companyBugsLoading : loading}
                    disableRowSelectionOnClick
                    initialState={{
                        sorting: { sortModel: [{ field: "createDate", sort: "desc" }] },
                    }}
                    localeText={localeText}
                />
            </Box>

            <BugDetailsDialog
                open={detailsOpen}
                bug={isCompany ? companyBugDetails : myBugDetails}
                loading={isCompany ? companyBugDetailsLoading : myBugDetailsLoading}
                error={isCompany ? !!companyBugDetailsError : !!myBugDetailsError}
                showReportedBy={isCompany}
                onClose={() => setDetailsOpen(false)}
                onExited={handleDetailsExited}
            />
        </Box>
    );
};
